import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import CityPage from "./pages/CityPage";
import LocationsPage from "./pages/LocationsPage";
import { ServicesListPage, ServiceDetailPage } from "./pages/ServicesPage";
import BlogListPage from "./pages/BlogListPage";
import BlogPostPage from "./pages/BlogPostPage";
import ReviewsPage from "./pages/ReviewsPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminComments from "./pages/admin/AdminComments";
import AdminContact from "./pages/admin/AdminContact";
import AdminCities from "./pages/admin/AdminCities";
import AdminSite from "./pages/admin/AdminSite";
import WelcomePage from "./pages/WelcomePage";
import PoolTipsPage from "./pages/PoolTipsPage";

const Public = ({ children }) => <Layout>{children}</Layout>;

// 301-style redirect: legacy /locations/:slug → /pool-service-:slug (canonical, keyword-rich)
const LegacyCityRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/pool-service-${slug}`} replace />;
};

// Catch-all matcher for keyword-rich city URLs of the form /pool-service-<slug>.
// React Router v7 requires dynamic segments to be a full path segment, so we
// match the literal-prefixed path here via location.pathname instead.
const PoolServiceCatchAll = () => {
  const location = useLocation();
  const m = location.pathname.match(/^\/pool-service-(.+)$/);
  if (m && m[1]) {
    return <Public><CityPage key={m[1]} slugOverride={m[1]} /></Public>;
  }
  // Unknown URL → render homepage (graceful fallback; could be a 404 page later)
  return <Public><HomePage /></Public>;
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors />
          <Routes>
            <Route path="/" element={<Public><HomePage /></Public>} />
            <Route path="/about" element={<Public><AboutPage /></Public>} />
            <Route path="/services" element={<Public><ServicesListPage /></Public>} />
            <Route path="/services/:serviceSlug" element={<Public><ServiceDetailPage /></Public>} />
            <Route path="/services/:serviceSlug/:citySlug" element={<Public><ServiceDetailPage /></Public>} />
            <Route path="/locations" element={<Public><LocationsPage /></Public>} />
            <Route path="/locations/:slug" element={<LegacyCityRedirect />} />
            <Route path="/blog" element={<Public><BlogListPage /></Public>} />
            <Route path="/blog/:slug" element={<Public><BlogPostPage /></Public>} />
            <Route path="/reviews" element={<Public><ReviewsPage /></Public>} />
            <Route path="/contact" element={<Public><ContactPage /></Public>} />
            <Route path="/pool-tips" element={<Public><PoolTipsPage /></Public>} />

            {/* Hidden customer welcome page — shared via SMS/email, not linked from nav */}
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/hello" element={<WelcomePage />} />
            <Route path="/hi" element={<WelcomePage />} />
            <Route path="/welcome-pink-truck-2026" element={<WelcomePage />} />
            <Route path="/thanks" element={<WelcomePage />} />
            <Route path="/thank-you" element={<WelcomePage />} />
            <Route path="/pool-guru" element={<WelcomePage />} />
            <Route path="/poolguru" element={<WelcomePage />} />
            <Route path="/welcome-v4" element={<WelcomePage />} />
            <Route path="/save-contact" element={<WelcomePage />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="comments" element={<AdminComments />} />
              <Route path="contact" element={<AdminContact />} />
              <Route path="cities" element={<AdminCities />} />
              <Route path="site" element={<AdminSite />} />
            </Route>
            {/* Catch-all: handle keyword-rich /pool-service-<slug> URLs (RR v7 doesn't support partial-segment params) */}
            <Route path="*" element={<PoolServiceCatchAll />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
